// App.jsx - Simplified without Timeline
import React, { useState, useEffect, useRef } from "react";
import Header from './components/Header';
import Sidebar from './components/Sidebar';
import FlowChart from './components/FlowChart';
import BlockEditor from './components/BlockEditor';
import PathExplorer from './components/PathExplorer';
import ScenarioValidator from './components/ScenarioValidator';

export default function App() {
  const [scenarioTitle, setScenarioTitle] = useState("New Crisis Scenario");
  const [blocks, setBlocks] = useState([]);
  const [activeView, setActiveView] = useState("builder");
  const [activeBlockId, setActiveBlockId] = useState(null);
  const [simulationRunning, setSimulationRunning] = useState(false);
  const [elapsedTime, setElapsedTime] = useState(0);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [validationIssues, setValidationIssues] = useState([]);
  const [scenarioStats, setScenarioStats] = useState({});
  
  const timerRef = useRef(null);
  const startTimeRef = useRef(null);

  // Initialize with sample data
  useEffect(() => {
    if (blocks.length === 0) {
      setBlocks([
        {
          id: "initial-crisis",
          type: "event",
          title: "Initial Crisis",
          description: "A fire has broken out in the main building",
          duration: 120, // 2 minutes
          injects: [
            { type: "text", content: "Fire alarm activates throughout the building", time: "0" },
            { type: "text", content: "First responders arrive on scene", time: "30" },
            { type: "text", content: "Fire department establishes command post", time: "60" }
          ],
          next: ["decision-1"],
          autoProceed: true,
          position: { x: 150, y: 150 }
        },
        {
          id: "decision-1",
          type: "choice",
          title: "Response Decision",
          description: "Choose the appropriate response to the fire",
          choices: [
            { label: "Evacuate immediately", targetId: "evacuation" },
            { label: "Attempt containment", targetId: "containment" },
            { label: "Gather more information", targetId: "assessment" }
          ],
          position: { x: 500, y: 150 }
        },
        {
          id: "evacuation",
          type: "event",
          title: "Evacuation Procedure",
          description: "Full building evacuation initiated",
          duration: 300, // 5 minutes
          injects: [
            { type: "text", content: "PA system announces evacuation", time: "0" },
            { type: "text", content: "Evacuation wardens guide personnel", time: "60" },
            { type: "text", content: "Emergency exits opened", time: "90" }
          ],
          next: [],
          autoProceed: true,
          position: { x: 850, y: 50 }
        },
        {
          id: "containment",
          type: "event",
          title: "Fire Containment",
          description: "Attempt to contain fire with available resources",
          duration: 600, // 10 minutes
          injects: [
            { type: "text", content: "Fire team deploys equipment", time: "0" },
            { type: "text", content: "Water supply activated", time: "120" },
            { type: "text", content: "Adjacent areas secured", time: "240" }
          ],
          next: [],
          autoProceed: true,
          position: { x: 850, y: 250 }
        },
        {
          id: "assessment",
          type: "event",
          title: "Situation Assessment",
          description: "Gather more information about the fire",
          duration: 180, // 3 minutes
          injects: [
            { type: "text", content: "Security team reports fire location", time: "0" },
            { type: "text", content: "Building sensors provide fire extent data", time: "60" },
            { type: "text", content: "Incident commander reviews options", time: "120" }
          ],
          next: ["decision-2"],
          autoProceed: true,
          position: { x: 850, y: 450 }
        },
        {
          id: "decision-2",
          type: "choice",
          title: "Updated Decision",
          description: "Based on new information, choose next action",
          choices: [
            { label: "Proceed with evacuation", targetId: "evacuation" },
            { label: "Continue containment efforts", targetId: "containment" }
          ],
          position: { x: 1200, y: 350 }
        }
      ]);
    }
  }, []);

  const runSimulation = () => {
    setSimulationRunning(true);
    startTimeRef.current = Date.now();
    setElapsedTime(0);
    
    // Start the simulation timer
    timerRef.current = setInterval(() => {
      const elapsed = Math.floor((Date.now() - startTimeRef.current) / 1000);
      setElapsedTime(elapsed);
    }, 100); // Update every 100ms for smooth updates
  };

  const pauseSimulation = () => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      setSimulationRunning(false);
    }
  };

  const resetSimulation = () => {
    pauseSimulation();
    setElapsedTime(0);
    setActiveBlockId(null);
  };

  // Handle validation changes
  const handleValidationChange = (issues, stats) => {
    setValidationIssues(issues);
    setScenarioStats(stats);
  };

  // Block manipulation functions
  const addBlock = (type = 'event') => {
    const newBlock = {
      id: `${type}-${Date.now()}`,
      type: type,
      title: type === 'event' ? 'New Event' : 'New Decision',
      description: "",
      position: { x: Math.random() * 200 + 150, y: Math.random() * 200 + 150 }
    };

    if (type === 'event') {
      newBlock.duration = 60; // Default 1 minute
      newBlock.injects = [];
      newBlock.next = [];
      newBlock.autoProceed = true;
    } else {
      newBlock.choices = [];
    }

    setBlocks([...blocks, newBlock]);
  };

  const updateBlock = (index, key, value) => {
    const updated = [...blocks];
    
    // Handle duration with min/max constraints
    if (key === 'duration') {
      const duration = Math.max(30, Math.min(1200, parseInt(value) || 30)); // 30 sec to 20 min
      updated[index][key] = duration;
    } else {
      updated[index][key] = value;
    }
    
    setBlocks(updated);
  };

  const removeBlock = (index) => {
    const blockId = blocks[index].id;
    const updated = blocks.filter((_, i) => i !== index).map(block => {
      const updatedBlock = { ...block };
      
      if (block.type === 'event' && block.next) {
        updatedBlock.next = block.next.filter(id => id !== blockId);
      }
      
      if (block.type === 'choice' && block.choices) {
        updatedBlock.choices = block.choices.filter(choice => choice.targetId !== blockId);
      }
      
      return updatedBlock;
    });
    setBlocks(updated);
  };

  // Event block functions with injects
  const addInject = (blockIndex) => {
    const updated = [...blocks];
    if (updated[blockIndex].type === 'event') {
      updated[blockIndex].injects = updated[blockIndex].injects || [];
      updated[blockIndex].injects.push({ type: "text", content: "", time: "0" });
      setBlocks(updated);
    }
  };

  const updateInject = (blockIndex, injectIndex, key, value) => {
    const updated = [...blocks];
    if (updated[blockIndex].type === 'event') {
      updated[blockIndex].injects[injectIndex][key] = value;
      setBlocks(updated);
    }
  };

  const removeInject = (blockIndex, injectIndex) => {
    const updated = [...blocks];
    if (updated[blockIndex].type === 'event') {
      updated[blockIndex].injects.splice(injectIndex, 1);
      setBlocks(updated);
    }
  };

  // Choice block functions
  const addChoice = (blockIndex) => {
    const updated = [...blocks];
    if (updated[blockIndex].type === 'choice') {
      updated[blockIndex].choices = updated[blockIndex].choices || [];
      updated[blockIndex].choices.push({ label: "", targetId: "" });
      setBlocks(updated);
    }
  };

  const updateChoice = (blockIndex, choiceIndex, key, value) => {
    const updated = [...blocks];
    if (updated[blockIndex].type === 'choice') {
      if (!updated[blockIndex].choices[choiceIndex]) {
        updated[blockIndex].choices[choiceIndex] = {};
      }
      updated[blockIndex].choices[choiceIndex][key] = value;
      setBlocks(updated);
    }
  };

  const removeChoice = (blockIndex, choiceIndex) => {
    const updated = [...blocks];
    if (updated[blockIndex].type === 'choice') {
      updated[blockIndex].choices.splice(choiceIndex, 1);
      setBlocks(updated);
    }
  };

  // Node manipulation functions
  const handleNodePositionChange = (nodeId, position) => {
    const updated = blocks.map(block => 
      block.id === nodeId ? { ...block, position } : block
    );
    setBlocks(updated);
  };

  // Handle connections created in the flow chart
  const handleBlockConnection = (sourceId, targetId, sourceHandleId = null) => {
    const sourceIndex = blocks.findIndex(b => b.id === sourceId);
    const sourceBlock = blocks[sourceIndex];
    const targetBlock = blocks.find(b => b.id === targetId);
    
    if (sourceIndex === -1 || !targetBlock) return;
    
    const updated = [...blocks];
    
    if (sourceBlock.type === 'event') {
      // Add as next connection for event blocks
      if (!updated[sourceIndex].next) {
        updated[sourceIndex].next = [];
      }
      
      if (!updated[sourceIndex].next.includes(targetId)) {
        updated[sourceIndex].next.push(targetId);
      }
    } else if (sourceBlock.type === 'choice') {
      // Add as choice connection
      if (!updated[sourceIndex].choices) {
        updated[sourceIndex].choices = [];
      }
      
      if (sourceHandleId && sourceHandleId.startsWith('choice-')) {
        const choiceIndex = parseInt(sourceHandleId.split('-')[1]);
        if (updated[sourceIndex].choices[choiceIndex]) {
          updated[sourceIndex].choices[choiceIndex].targetId = targetId;
        }
      } else {
        let choiceIndex = updated[sourceIndex].choices.findIndex(c => !c.targetId);
        if (choiceIndex === -1) {
          updated[sourceIndex].choices.push({
            label: `Go to ${targetBlock.title || targetBlock.id}`,
            targetId: targetId
          });
        } else {
          updated[sourceIndex].choices[choiceIndex].targetId = targetId;
        }
      }
    }
    
    setBlocks(updated);
  };

  // Handle path explorer step
  const handlePathStep = (block, path) => {
    setActiveBlockId(block.id);
  };

  const exportJSON = () => {
    const scenario = { title: scenarioTitle, blocks };
    const blob = new Blob([JSON.stringify(scenario, null, 2)], {
      type: "application/json"
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${scenarioTitle.replace(/\s+/g, "_")}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // JSON Import function
  const importJSON = (data) => {
    try {
      // Validate the data structure
      if (!data.title || !data.blocks || !Array.isArray(data.blocks)) {
        throw new Error('Invalid JSON format: Missing title or blocks array');
      }

      // Validate each block
      const validatedBlocks = data.blocks.map(block => {
        if (!block.id || !block.type) {
          throw new Error('Invalid block: Missing id or type');
        }

        // Create a validated block with defaults
        const validatedBlock = {
          id: block.id,
          type: block.type,
          title: block.title || '',
          description: block.description || '',
          position: block.position || { x: Math.random() * 200 + 150, y: Math.random() * 200 + 150 }
        };

        // Add type-specific properties
        if (block.type === 'event') {
          validatedBlock.duration = Math.max(30, Math.min(1200, block.duration || 60));
          validatedBlock.injects = block.injects || [];
          validatedBlock.next = block.next || [];
          validatedBlock.autoProceed = block.autoProceed !== undefined ? block.autoProceed : true;
        } else if (block.type === 'choice') {
          validatedBlock.choices = block.choices || [];
        }

        return validatedBlock;
      });

      // Import the validated data
      setBlocks(validatedBlocks);
      setScenarioTitle(data.title);
      
      // Reset simulation state
      resetSimulation();
      
      // Notify user of success
      alert('Scenario imported successfully!');
      
    } catch (error) {
      console.error('Import error:', error);
      alert('Error importing scenario: ' + error.message);
    }
  };

  return (
    <div className="bg-gray-100 min-h-screen">
      <Header
        scenarioTitle={scenarioTitle}
        setScenarioTitle={setScenarioTitle}
        drawerOpen={drawerOpen}
        setDrawerOpen={setDrawerOpen}
        elapsedTime={elapsedTime}
        exportJSON={exportJSON}
        importJSON={importJSON}
      />

      <div className="flex h-[calc(100vh-64px)]">
        <Sidebar
          drawerOpen={drawerOpen}
          activeView={activeView}
          setActiveView={setActiveView}
          addBlock={addBlock}
          simulationRunning={simulationRunning}
          runSimulation={runSimulation}
          pauseSimulation={pauseSimulation}
          resetSimulation={resetSimulation}
          validationIssues={validationIssues}
          scenarioStats={scenarioStats}
        />

        <div className="flex-1 overflow-hidden">
          {activeView === "flow" ? (
            <FlowChart
              blocks={blocks}
              activeBlockId={activeBlockId}
              onNodePositionChange={handleNodePositionChange}
              onBlockConnection={handleBlockConnection}
            />
          ) : activeView === "explorer" ? (
            <PathExplorer
              blocks={blocks}
              simulationRunning={simulationRunning}
              currentTime={elapsedTime}
              onPathStep={handlePathStep}
            />
          ) : activeView === "validator" ? (
            <ScenarioValidator
              blocks={blocks}
              onValidationChange={handleValidationChange}
            />
          ) : (
            <BlockEditor
              blocks={blocks}
              updateBlock={updateBlock}
              removeBlock={removeBlock}
              addInject={addInject}
              updateInject={updateInject}
              removeInject={removeInject}
              addChoice={addChoice}
              updateChoice={updateChoice}
              removeChoice={removeChoice}
            />
          )}
        </div>
      </div>
    </div>
  );
}